/**
 * Staging transforms: accessory consolidation, catalogue image propagation, review hierarchy.
 */
import { buildDtoolsProjectCatalogKey } from "./dtoolsProjectCatalogKey";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import { parseCustomerExplanationStored } from "./proposalItemCleoDescription";
import { formatDtoolsHierarchicalRoomLabel } from "./dtoolsLocationHierarchy";
import { dtoolsRoomIdentity, dtoolsSystemIdentity } from "./dtoolsHierarchyOrdering";

export type DtoolsMergedSourceLine = {
  dtoolsItemId: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  dtoolsDescription: string;
};

export type DtoolsReviewPrimary = {
  line: DtoolsStagingLine;
  accessories: DtoolsStagingLine[];
};

export type DtoolsReviewRoom = {
  locationId: string | null;
  /** Hierarchical display name (FullName preferred). */
  name: string;
  /** Short D-Tools Location.Name when known. */
  locationName?: string | null;
  /** D-Tools Location.FullName when known. */
  locationFullName?: string | null;
  /** Parent location Id for sub-rooms. */
  parentLocationId?: string | null;
  primaries: DtoolsReviewPrimary[];
};

export type DtoolsReviewSystem = {
  systemId: string | null;
  name: string;
  rooms: DtoolsReviewRoom[];
  systemLevelPrimaries: DtoolsReviewPrimary[];
};

export type DtoolsReviewFooter = {
  adjustments: DtoolsStagingLine[];
  optional: DtoolsStagingLine[];
  nonBillable: DtoolsStagingLine[];
};

const FOOTER_BUCKETS = new Set(["discount", "optional", "non_billable"]);

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function pricesMatch(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.005;
}

export function ensureMergedSources(line: DtoolsStagingLine): DtoolsStagingLine {
  if (line.mergedSources?.length) return line;
  return {
    ...line,
    mergedSources: [
      {
        dtoolsItemId: line.dtoolsItemId,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        lineTotal: line.lineTotal,
        dtoolsDescription: line.dtoolsDescription,
      },
    ],
  };
}

/** Product identity for accessory merge — excludes per-line SI item id so duplicates consolidate. */
export function buildAccessoryProductIdentityKey(line: DtoolsStagingLine): string {
  return buildDtoolsProjectCatalogKey({
    manufacturer: line.manufacturer,
    model: line.model,
    catalogId: line.catalogId,
    componentId: line.componentId,
    displayName: [line.manufacturer, line.model].filter(Boolean).join(" "),
  });
}

/** Consolidation key for accessories under the same parent (same product + unit price). */
export function accessoryConsolidationKey(line: DtoolsStagingLine): string | null {
  if (!line.parentId) return null;
  if (FOOTER_BUCKETS.has(line.bucket)) return null;
  const productKey = buildAccessoryProductIdentityKey(line);
  return `${line.parentId}|${productKey}|${roundMoney(line.unitPrice).toFixed(4)}`;
}

/** Grouping key for identical primary products within the same system + room (review display only). */
export function buildPrimaryProductGroupingKey(line: DtoolsStagingLine): string {
  const catalogueKey = String(line.catalogueKey ?? "").trim();
  const price = roundMoney(line.unitPrice).toFixed(4);
  const vo = line.variationOrderAnnotation;
  // Keep VO-traced rows distinct so removed/added history is never merged away.
  const voKey = vo ? `|vo:${vo.kind}:${vo.voId}:${vo.voNumber}` : "";
  if (catalogueKey) return `ck:${catalogueKey}|${price}${voKey}`;
  return `prod:${buildAccessoryProductIdentityKey(line)}|${price}${voKey}`;
}

function accessoryDisplayGroupKey(line: DtoolsStagingLine): string {
  return `${buildAccessoryProductIdentityKey(line)}|${roundMoney(line.unitPrice).toFixed(4)}`;
}

function pickCleoFieldsFromMergedGroup(group: DtoolsStagingLine[], first: DtoolsStagingLine): {
  cleoDescription: string | null | undefined;
  customerExplanation: string | null | undefined;
} {
  const cleoDescription =
    group.find((row) => String(row.cleoDescription ?? "").trim())?.cleoDescription?.trim() ??
    first.cleoDescription;
  const customerExplanation =
    group.find((row) => parseCustomerExplanationStored(row.customerExplanation))?.customerExplanation?.trim() ??
    group.find((row) => String(row.customerExplanation ?? "").trim())?.customerExplanation?.trim() ??
    first.customerExplanation;
  return { cleoDescription, customerExplanation };
}

function mergeStagingLineGroup(group: DtoolsStagingLine[]): DtoolsStagingLine {
  const first = ensureMergedSources(group[0]!);
  const mergedSources: DtoolsMergedSourceLine[] = [];
  for (const row of group) {
    const src = ensureMergedSources(row);
    mergedSources.push(...src.mergedSources);
  }

  const quantity = mergedSources.reduce((s, m) => s + m.quantity, 0);
  const lineTotal = roundMoney(mergedSources.reduce((s, m) => s + m.lineTotal, 0));
  const unitPrice = first.unitPrice;

  const descriptions = [...new Set(mergedSources.map((m) => m.dtoolsDescription.trim()).filter(Boolean))];
  const dtoolsDescription =
    descriptions.length <= 1 ? (descriptions[0] ?? first.dtoolsDescription) : descriptions[0] ?? first.dtoolsDescription;

  const polishedDescriptions = [...new Set(group.map((g) => g.polishedDescription.trim()).filter(Boolean))];
  const polishedDescription = polishedDescriptions[0] ?? first.polishedDescription;
  const { cleoDescription, customerExplanation } = pickCleoFieldsFromMergedGroup(group, first);
  const catalogProductId =
    group.find((g) => g.catalogProductId != null && Number(g.catalogProductId) > 0)?.catalogProductId ??
    first.catalogProductId;
  const catalogMatchConfidence =
    group.find((g) => g.catalogMatchConfidence)?.catalogMatchConfidence ?? first.catalogMatchConfidence;

  return {
    ...first,
    id: first.id,
    quantity,
    unitPrice,
    lineTotal,
    dtoolsDescription,
    polishedDescription,
    cleoDescription,
    customerExplanation,
    mergedSources,
    imageUrl: group.find((g) => g.imageUrl)?.imageUrl ?? first.imageUrl,
    projectCatalogueId:
      group.find((g) => g.projectCatalogueId)?.projectCatalogueId ?? first.projectCatalogueId,
    catalogProductId,
    catalogMatchConfidence,
  };
}

function mergeAccessoryGroup(group: DtoolsStagingLine[]): DtoolsStagingLine {
  return { ...mergeStagingLineGroup(group), isAccessory: true };
}

function consolidateAccessoriesForReviewDisplay(accessories: DtoolsStagingLine[]): DtoolsStagingLine[] {
  const groups = new Map<string, DtoolsStagingLine[]>();
  for (const line of accessories) {
    const key = accessoryDisplayGroupKey(line);
    const list = groups.get(key) ?? [];
    list.push(line);
    groups.set(key, list);
  }
  const merged: DtoolsStagingLine[] = [];
  for (const group of groups.values()) {
    merged.push(group.length === 1 ? group[0]! : mergeAccessoryGroup(group));
  }
  return merged.sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Presentation-only: merge identical primary blocks (same catalogue + unit price), summing qty and lineTotal. */
export function consolidateIdenticalPrimaryBlocks(primaries: DtoolsReviewPrimary[]): DtoolsReviewPrimary[] {
  const groups = new Map<string, DtoolsReviewPrimary[]>();
  for (const block of primaries) {
    const key = buildPrimaryProductGroupingKey(block.line);
    const list = groups.get(key) ?? [];
    list.push(block);
    groups.set(key, list);
  }

  const out: DtoolsReviewPrimary[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      out.push(group[0]!);
      continue;
    }
    const mergedLine = mergeStagingLineGroup(group.map((g) => g.line));
    const mergedAccessories = consolidateAccessoriesForReviewDisplay(group.flatMap((g) => g.accessories));
    out.push({ line: mergedLine, accessories: mergedAccessories });
  }
  return out.sort((a, b) => a.line.sortOrder - b.line.sortOrder);
}

/**
 * Presentation-only: merge duplicate primary/accessory cards within each system room.
 * Does not mutate staging lines used for import.
 */
export function groupIdenticalProductsInReviewHierarchy(result: {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
}): {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
} {
  return {
    ...result,
    systems: result.systems.map((sys) => ({
      ...sys,
      rooms: sys.rooms.map((room) => ({
        ...room,
        primaries: consolidateIdenticalPrimaryBlocks(room.primaries),
      })),
      systemLevelPrimaries: consolidateIdenticalPrimaryBlocks(sys.systemLevelPrimaries),
    })),
    footer: result.footer,
  };
}

/**
 * Merge identical accessories under the same parent (same catalog key + unit price).
 * Primary products are never consolidated.
 */
export function consolidateStagingAccessoryLines(lines: DtoolsStagingLine[]): DtoolsStagingLine[] {
  const normalized = lines.map(ensureMergedSources);
  const accessories: DtoolsStagingLine[] = [];
  const kept: DtoolsStagingLine[] = [];

  for (const line of normalized) {
    const key = accessoryConsolidationKey(line);
    if (key) accessories.push(line);
    else kept.push(line);
  }

  const groups = new Map<string, DtoolsStagingLine[]>();
  for (const line of accessories) {
    const key = accessoryConsolidationKey(line)!;
    const list = groups.get(key) ?? [];
    list.push(line);
    groups.set(key, list);
  }

  const consolidatedByKey = new Map<string, DtoolsStagingLine>();
  const absorbedIds = new Set<string>();

  for (const [key, group] of groups) {
    if (group.length === 1) {
      consolidatedByKey.set(key, group[0]!);
      continue;
    }
    const merged = mergeAccessoryGroup(group);
    consolidatedByKey.set(key, merged);
    for (const row of group) {
      if (row.id !== merged.id) absorbedIds.add(row.id);
    }
  }

  const result: DtoolsStagingLine[] = [];
  const insertedKeys = new Set<string>();

  for (const line of normalized) {
    if (absorbedIds.has(line.id)) continue;
    const key = accessoryConsolidationKey(line);
    if (key && consolidatedByKey.has(key)) {
      if (!insertedKeys.has(key)) {
        result.push(consolidatedByKey.get(key)!);
        insertedKeys.add(key);
      }
      continue;
    }
    result.push(line);
  }

  return result.map((line, i) => ({ ...line, sortOrder: i }));
}

export function applyCatalogueMatchToLines(
  lines: DtoolsStagingLine[],
  catalogueKey: string,
  patch: Partial<
    Pick<
      DtoolsStagingLine,
      | "imageUrl"
      | "projectCatalogueId"
      | "polishedDescription"
      | "cleoDescription"
      | "customerExplanation"
      | "catalogMatchConfidence"
    >
  >,
): { lines: DtoolsStagingLine[]; matchCount: number } {
  let matchCount = 0;
  const next = lines.map((line) => {
    if (line.catalogueKey !== catalogueKey) return line;
    matchCount += 1;
    const merged: DtoolsStagingLine = { ...line };
    if (patch.imageUrl !== undefined) merged.imageUrl = patch.imageUrl;
    if (patch.projectCatalogueId !== undefined) {
      merged.projectCatalogueId = patch.projectCatalogueId;
      if (patch.projectCatalogueId != null && patch.projectCatalogueId > 0) {
        merged.catalogMatchConfidence = patch.catalogMatchConfidence ?? "matched";
      }
    }
    if (patch.catalogMatchConfidence !== undefined) {
      merged.catalogMatchConfidence = patch.catalogMatchConfidence;
    }
    if (patch.polishedDescription?.trim()) merged.polishedDescription = patch.polishedDescription.trim();
    if (patch.cleoDescription?.trim() && !String(line.cleoDescription ?? "").trim()) {
      merged.cleoDescription = patch.cleoDescription.trim();
      if (!String(line.polishedDescription ?? "").trim()) {
        merged.polishedDescription = patch.cleoDescription.trim();
      }
    }
    if (patch.customerExplanation?.trim() && !String(line.customerExplanation ?? "").trim()) {
      merged.customerExplanation = patch.customerExplanation.trim();
    }
    return merged;
  });
  return { lines: next, matchCount };
}

export function buildReviewHierarchy(lines: DtoolsStagingLine[]): {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
} {
  const footer: DtoolsReviewFooter = {
    adjustments: [],
    optional: [],
    nonBillable: [],
  };

  const hierarchyLines: DtoolsStagingLine[] = [];
  for (const line of lines) {
    if (line.bucket === "discount") footer.adjustments.push(line);
    else if (line.bucket === "optional") footer.optional.push(line);
    else if (line.bucket === "non_billable") footer.nonBillable.push(line);
    else hierarchyLines.push(line);
  }

  const byId = new Map(hierarchyLines.map((l) => [l.id, l]));
  const childrenOf = new Map<string, DtoolsStagingLine[]>();
  for (const line of hierarchyLines) {
    if (!line.parentId) continue;
    const list = childrenOf.get(line.parentId) ?? [];
    list.push(line);
    childrenOf.set(line.parentId, list);
  }

  const primaries = hierarchyLines
    .filter((l) => !l.parentId || !byId.has(l.parentId))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));

  const systemMap = new Map<string, DtoolsReviewSystem>();

  function ensureSystem(systemId: string | null, systemName: string | null): DtoolsReviewSystem {
    const key = dtoolsSystemIdentity({ systemId, systemName });
    let sys = systemMap.get(key);
    if (!sys) {
      sys = {
        systemId,
        name: systemName ?? "Unassigned system",
        rooms: [],
        systemLevelPrimaries: [],
      };
      systemMap.set(key, sys);
    }
    return sys;
  }

  function ensureRoom(
    sys: DtoolsReviewSystem,
    locationId: string | null,
    line: Pick<DtoolsStagingLine, "roomName" | "locationName" | "locationFullName" | "parentLocationId">,
  ): DtoolsReviewRoom {
    const roomKey =
      dtoolsRoomIdentity({ locationId, roomName: line.roomName }) ??
      `name:${String(line.roomName ?? "No room")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()}`;
    let room = sys.rooms.find((r) => {
      const existingKey =
        dtoolsRoomIdentity({ locationId: r.locationId, roomName: r.name }) ??
        `name:${r.name
          .trim()
          .replace(/\s+/g, " ")
          .toLowerCase()}`;
      return existingKey === roomKey;
    });
    const resolvedName = (() => {
      const hierarchical = formatDtoolsHierarchicalRoomLabel(line);
      if (hierarchical && hierarchical !== "No room" && !/^\d+$/.test(hierarchical)) return hierarchical;
      if (locationId?.startsWith("name:")) return locationId.slice("name:".length);
      return hierarchical || (locationId ? "Room not available" : "No room");
    })();
    if (!room) {
      room = {
        locationId,
        name: resolvedName,
        locationName: line.locationName ?? null,
        locationFullName: line.locationFullName ?? null,
        parentLocationId: line.parentLocationId ?? null,
        primaries: [],
      };
      sys.rooms.push(room);
    } else {
      if (
        (/^\d+$/.test(room.name) || room.name === "Room not available") &&
        resolvedName &&
        !/^\d+$/.test(resolvedName) &&
        resolvedName !== "Room not available"
      ) {
        room.name = resolvedName;
      }
      if (!room.locationFullName && line.locationFullName) room.locationFullName = line.locationFullName;
      if (!room.locationName && line.locationName) room.locationName = line.locationName;
      if (!room.parentLocationId && line.parentLocationId) room.parentLocationId = line.parentLocationId;
    }
    return room;
  }

  for (const primary of primaries) {
    const accessories = (childrenOf.get(primary.id) ?? []).sort((a, b) =>
      a.sortOrder - b.sortOrder,
    );
    const block: DtoolsReviewPrimary = { line: primary, accessories };
    const sys = ensureSystem(primary.systemId, primary.systemName);
    if (dtoolsRoomIdentity(primary) != null) {
      ensureRoom(sys, primary.locationId, primary).primaries.push(block);
    } else {
      sys.systemLevelPrimaries.push(block);
    }
  }

  // Map insertion order follows first appearance by sortOrder — never alphabetical.
  return { systems: [...systemMap.values()], footer };
}
